import { useState, useContext, useEffect, useMemo } from "react";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import DataTable from "cxfcomponents/Tables/DataTable";
import StatusBadge from "cxfcomponents/StatusBadge";
import DefaultUserAvatar from "layouts/pages/account/components/Header/components/DefaultUserAvatar";
import { ThemedMailIcon } from "assets/CXFIcons/Static";
import { ThemedRemoveCircleOutlineIcon } from "assets/CXFIcons/Actions";
import { useSlugFlow } from "hooks/useSlugFlow";
import { useRoles } from "hooks/useRoles";
import { useUserFlows } from "hooks/useUserFlows";
import { PERMISSIONS } from "constants/portal";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import { allowAction } from "helpers/common";
import { useAuth0 } from "@auth0/auth0-react";
import UsersModal2 from "layouts/Flows/components/UsersModal/index2";
import { RemoveUserDialog } from "../../removeUser";
import { unshareFlow, updateUserFlows } from "api/FlowDesignerApis";
import { toast } from "react-toastify";
import { useDetermineShareFlowUserAccessType } from "hooks/useDetermineShareFlowUserAccessType";
import { useSelector } from "react-redux";
import { useResourceListener } from "hooks/useResourceListener";

const permissionTypes = {
    edit: PERMISSIONS.EDIT_WORKFLOWS,
    share: PERMISSIONS.SHARE_WORKFLOWS,
    publish: PERMISSIONS.PUBLISH_FLOW,
    retrieve: PERMISSIONS.RETRIEVE_WORKFLOWS,
    delete: PERMISSIONS.DELETE_WORKFLOWS,
    activate: PERMISSIONS.ACTIVATE_WORKFLOWS,
    deactivate: PERMISSIONS.DEACTIVATE_WORKFLOWS,
};

const accessTypeOptions = [
    { value: "readOnly", label: "Read Only" },
    { value: "editor", label: "Editor" },
];

const UserRole = ({
    user,
    isAllowedAction,
    handleSetUserToBeRemoved,
    isReadOnlyAccess,
}) => {
  const [accessType, setAccessType] = useState(accessTypeOptions[0]);
  const [isUpdatingAccessType, setIsUpdatingAccessType] = useState(false);

  const handleUpdateUserAccessType = async (newValue) => {
    if (isUpdatingAccessType || !user.userflow) return;

    try {
      setIsUpdatingAccessType(true);

      const { tenant_id, ...rest } = user.userflow;

      const updatedUserFlow = await updateUserFlows(user.tenant_id, {
        ...rest,
        access_type: newValue.value,
      });

      if (!updatedUserFlow) throw new Error("User flow update failed");

      setAccessType(newValue);
      toast.success("User access type successfully updated");
    } catch (error) {
      toast.error("Failed to update user access type");
    } finally {
    setIsUpdatingAccessType(false);
    }
  };

  useEffect(() => {
    setAccessType(
      accessTypeOptions.find((status) => status.value === user.userflow?.access_type) ??
        accessTypeOptions[0]
    );
  }, [user.userflow?.access_type]);

  return (
    <SoftBox
      sx={{
        alignItems: "center",
        display: "flex",
        width: "140px",
      }}
    >
      <SoftSelect
        className="flow-user-select"
        placeholder="Filter Status"
        options={[...accessTypeOptions].sort((a, b) => a.label.localeCompare(b.label))}
        hideSelectedOptions={false}
        onChange={handleUpdateUserAccessType}
        value={accessType}
        isDisabled={user.isOwner || !isAllowedAction("share") || isReadOnlyAccess}
      />

      {!user.isOwner && isAllowedAction("share") && !isReadOnlyAccess && (
        <button
          className="flow-user-remove"
          style={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
          }}
          onClick={() => handleSetUserToBeRemoved(user)}
        >
          <ThemedRemoveCircleOutlineIcon width="32px" height="32px" />
        </button>
      )}
    </SoftBox>
  );
};

export const Users = () => {
  const { flow } = useSlugFlow();
  const [searchName, setSearchName] = useState("");
  const { users } = useSelector((store) => store.portal.users);
  const { userFlows, handleFetchUserFlows } = useUserFlows();

  const [flowUserPermissions, setFlowUserPermissions] = useState({
    allowEdit: false,
    allowShare: false,
    allowPublish: false,
    allowRetrieve: false,
    allowDelete: false,
    allowActivate: false,
    allowDeactivate: false,
  });

  const { userPermissions } = useContext(PermissionsContext);
  const { user } = useAuth0();
  const { roles } = useRoles(false);
  const { isReadOnlyAccess } = useDetermineShareFlowUserAccessType(flow?.id);
  const [openUsersModal, setOpenUsersModal] = useState(false);
  const [shareList, setShareList] = useState(null);
  const [userToBeRemoved, setUserToBeRemoved] = useState(null);

  useResourceListener("flowsSharingUpdate", handleFetchUserFlows, 250);

  const handleAddUsers = () => {
    setOpenUsersModal(true);
  };

  const isAllowedAction = (action) => {
    switch (action) {
      case "share": {
        if (
          allowAction(
            user?.cxfUserId,
            flow?.created_by_id,
            userPermissions,
            PERMISSIONS.SHARE_ANY_WORKFLOWS
          ) ||
          flowUserPermissions.allowShare
        ) {
          return true;
        }
        return false;
      }

      default:
        return false;
    }
  };

  const handleSetUserToBeRemoved = (selectedUser) => {
    setUserToBeRemoved(selectedUser);
  };

  const handleRemoveUser = async () => {
    if (!userToBeRemoved?.userflow?.id) return;

    await unshareFlow(user?.stripeId, [userToBeRemoved.userflow.id]);
    setUserToBeRemoved(null);
    toast.success("User has been successfully removed from the flow");
  };

  const normalizedUsers = useMemo(() => {
    if (!flow || !users) return [];

    const searchValue = searchName.trim().toLowerCase();

    const filteredUsers = users.filter((currentUser) => {
      if (!searchValue) return true;

      return (
        currentUser.first_name?.toLowerCase().includes(searchValue) ||
        currentUser.last_name?.toLowerCase().includes(searchValue) ||
        currentUser.email_address?.toLowerCase().includes(searchValue)
      );
    });

    let owner = filteredUsers.find((currentUser) => currentUser.auth0_id === flow.created_by_id);

    if (owner) {
      owner = {
        ...owner,
        isOwner: true,
      };
    }

    const usersSharedWithFlow = userFlows.filter((userFlow) => userFlow.flow_id === flow.id);

    const sharedUsers = filteredUsers
      .filter((currentUser) =>
        usersSharedWithFlow.find((sharedUser) => sharedUser.user_id === currentUser.auth0_id)
      )
      .map((currentUser) => {
        const foundUserFlow = usersSharedWithFlow.find(
          (sharedUser) => sharedUser.user_id === currentUser.auth0_id
        );

        return {
          ...currentUser,
          userflow: foundUserFlow || currentUser.userflow,
        };
      });

    const ownerAlreadyInSharedUsers = sharedUsers.some(
      (sharedUser) => sharedUser.auth0_id === owner?.auth0_id
    );

    return [
      ...(owner && !ownerAlreadyInSharedUsers ? [owner] : []),
      ...sharedUsers,
    ];
  }, [flow, users, userFlows, searchName]);

  const tableData = useMemo(() => {
    return {
      columns: [
        {
          Header: "profile",
          accessor: "profile",
          canGroupBy: false,
          disableSortBy: true,
          Cell: ({ value, row }) =>
            value ? (
              <SoftBox
                component="img"
                src={value}
                alt={`${row?.original?.user?.first_name} ${row?.original?.user?.last_name}`}
                sx={{
                  border: ".5px solid white",
                  borderRadius: "12px",
                  width: "60px",
                  height: "60px",
                  objectFit: "cover",
                  marginRight: "10px",
                }}
              />
            ) : (
              <DefaultUserAvatar
                firstName={row?.original?.user?.first_name}
                lastName={row?.original?.user?.last_name}
                variant="rounded"
                size="lg"
                border=".5px solid white !important"
                disableFocusListener={false}
                disableTouchListener={false}
                open={false}
              />
            ),
        },
        {
          Header: "name",
          accessor: "name",
          width: 200,
          canGroupBy: false,
          Cell: ({ value }) => (
            <SoftBox
              className="white-to-dark"
              sx={{
                minWidth: "150px",
                maxWidth: "220px",
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
            >
              <SoftTypography
                sx={{ color: "#2D3748", fontSize: "14px", lineHeight: 1 }}
                className="white-to-dark"
                fontWeight="bold"
              >
                {value}
              </SoftTypography>
            </SoftBox>
          ),
        },
        {
          Header: "email",
          accessor: "email",
          width: 220,
          canGroupBy: false,
          Cell: ({ value }) => (
            <SoftTypography
              color="secondary"
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <ThemedMailIcon width="19px" height="19px" sx={{ marginRight: "8px" }} />
              {value}
            </SoftTypography>
          ),
        },
        {
          Header: "status",
          accessor: "status",
          canGroupBy: false,
          Cell: ({ value }) => <StatusBadge status={value} />,
        },
        {
          Header: "role",
          accessor: "user",
          canGroupBy: false,
          disableSortBy: true,
          Cell: ({ value }) => (
            <UserRole
              user={value}
              handleSetUserToBeRemoved={handleSetUserToBeRemoved}
              isAllowedAction={isAllowedAction}
              isReadOnlyAccess={isReadOnlyAccess}
            />
          ),
        },
      ].map((column) => ({
        ...column,
        disableResize: true,
      })),
      rows: normalizedUsers.map((currentUser) => ({
        profile: currentUser.image,
        name: `${currentUser.first_name ?? ""} ${currentUser.last_name ?? ""}`,
        email: currentUser.email_address,
        status: currentUser.status,
        user: currentUser,
      })),
    };
  }, [normalizedUsers, isReadOnlyAccess]);

  useEffect(() => {
    if (!userPermissions) return;

    const permissions = {
      allowEdit: false,
      allowShare: false,
      allowPublish: false,
      allowRetrieve: false,
      allowDelete: false,
      allowActivate: false,
      allowDeactivate: false,
    };

    userPermissions.forEach((permission) => {
      switch (permission.name) {
        case permissionTypes.edit:
          permissions.allowEdit = permission.value;
          break;
        case permissionTypes.share:
          permissions.allowShare = permission.value;
          break;
        case permissionTypes.publish:
          permissions.allowPublish = permission.value;
          break;
        case permissionTypes.retrieve:
          permissions.allowRetrieve = permission.value;
          break;
        case permissionTypes.delete:
          permissions.allowDelete = permission.value;
          break;
        case permissionTypes.activate:
          permissions.allowActivate = permission.value;
          break;
        case permissionTypes.deactivate:
          permissions.allowDeactivate = permission.value;
          break;
        default:
          break;
      }
    });

    setFlowUserPermissions(permissions);
  }, [userPermissions]);

  return (
    <>
      <SoftBox>
        <SoftBox
          sx={{
            alignItems: "flex-start",
            display: "flex",
            justifyContent: "flex-start",
            padding: "12px 0 12px 40px",
            marginBottom: "1rem",
          }}
        >
          <SoftInput
            size="medium"
            placeholder="Search..."
            value={searchName}
            onChange={({ currentTarget }) => {
              setSearchName(currentTarget.value);
            }}
            sx={{
              maxWidth: "380px",
              marginRight: "34px",
            }}
          />

          {isAllowedAction("share") && !isReadOnlyAccess && (
            <SoftButton variant="outlined" color="info" onClick={handleAddUsers}>
              add
            </SoftButton>
          )}
        </SoftBox>

        <DataTable
          table={tableData}
          entriesPerPage={{ defaultValue: 25 }}
          hiddenTopButtons={true}
          noTopPart
          tableHeight="clamp(350px, calc(100vh - 130px), calc(100vh - 500px))"
        />
      </SoftBox>

      <UsersModal2
        users={users || []}
        roles={roles}
        userFlows={userFlows}
        selectedUserFlow={flow}
        openUsersModal={openUsersModal}
        setOpenUsersModal={setOpenUsersModal}
        titleName={"Share"}
        shareList={shareList}
        flowView={1}
        setShareList={setShareList}
      />

      <RemoveUserDialog
        isOpen={!!userToBeRemoved}
        user={userToBeRemoved}
        onClose={() => setUserToBeRemoved(null)}
        onAccept={handleRemoveUser}
      />
    </>
  );
};
